/**
 * Module Manager — Settings → Workspace → Module Manager.
 *
 * Admin-only. Toggles which optional modules appear in the sidebar.
 * Core modules are always ON and rendered as locked read-only rows so
 * admins understand they can't be turned off (spec: "Cannot be disabled").
 *
 * Every toggle calls PATCH /workspace/modules and refreshes the workspace
 * context so the sidebar responds instantly without a page reload.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { useWorkspace } from "@/lib/workspace";
import { Lock } from "lucide-react";

export default function ModuleManager() {
  const { user } = useAuth();
  const workspace = useWorkspace();
  const [saving, setSaving] = useState(null);

  const isAdmin = user?.role === "admin";

  const { core, optional } = useMemo(() => {
    const core = workspace.catalog.filter((m) => m.core);
    const optional = workspace.catalog.filter((m) => !m.core);
    return { core, optional };
  }, [workspace.catalog]);

  const toggle = async (id, next) => {
    if (!isAdmin) return;

    // If we're DISABLING a parent that has dependent children currently on,
    // preview the cascade and get explicit confirmation. The backend will
    // enforce it too, but showing the impact up-front avoids surprises.
    if (!next) {
      try {
        const { data } = await api.get(`/workspace/modules/preview-disable/${id}`);
        const affected = data.cascaded_off || [];
        if (affected.length > 0) {
          const names = affected.map((cid) => {
            const m = workspace.catalog.find((c) => c.id === cid);
            return m ? m.label : cid;
          }).join(", ");
          const proceed = window.confirm(
            `Disabling this module will also disable:\n\n• ${names}\n\nAll data is preserved and can be restored by re-enabling. Continue?`
          );
          if (!proceed) return;
        }
      } catch { /* preview failed — fall through, backend still enforces */ }
    }

    setSaving(id);
    const nextSet = new Set(workspace.enabled);
    if (next) nextSet.add(id); else nextSet.delete(id);
    try {
      const { data } = await api.patch("/workspace/modules", { enabled_modules: [...nextSet] });
      await workspace.refresh();
      const cascaded = data?.cascaded_off || [];
      if (cascaded.length > 0) {
        toast.success(`${id} disabled · Auto-disabled: ${cascaded.join(", ")}`);
      } else {
        toast.success(`${id} ${next ? "enabled" : "disabled"}`);
      }
    } catch (e) {
      toast.error(apiError(e, "Could not update module"));
    } finally { setSaving(null); }
  };

  return (
    <div className="space-y-6" data-testid="module-manager">
      <div>
        <div className="overline text-ink-soft">Workspace</div>
        <h1 className="mt-2 font-display text-3xl tracking-tight">Module Manager</h1>
        <p className="mt-2 text-sm text-ink-mid max-w-2xl">
          Only turn on what your clinic actually uses. Disabled modules stay
          hidden from the sidebar until you re-enable them here — your data is
          never deleted.
        </p>
      </div>

      {!isAdmin && (
        <div className="rounded-2xl border border-[#EDC7C6] bg-[#FCECEC] px-4 py-3 text-sm text-[#B94A48]" data-testid="mm-readonly-notice">
          Module toggles are admin-only. You can see the current state but not edit it.
        </div>
      )}

      {/* Core — locked ON */}
      <Section title="Core modules" caption="Cannot be disabled — Viyora needs these.">
        <div className="grid md:grid-cols-2 gap-3" data-testid="mm-core-list">
          {core.map((m) => (
            <ModuleRow key={m.id} module={m} enabled={true} locked />
          ))}
        </div>
      </Section>

      {/* Optional — toggleable */}
      <Section title="Optional modules" caption="Enable only what your clinic uses.">
        <div className="grid md:grid-cols-2 gap-3" data-testid="mm-optional-list">
          {optional.map((m) => (
            <ModuleRow
              key={m.id}
              module={m}
              enabled={workspace.enabled.has(m.id)}
              onToggle={(next) => toggle(m.id, next)}
              saving={saving === m.id}
              readOnly={!isAdmin}
            />
          ))}
        </div>
      </Section>
    </div>
  );
}

function Section({ title, caption, children }) {
  return (
    <div>
      <div className="mb-3">
        <div className="text-sm font-medium">{title}</div>
        {caption && <div className="text-xs text-ink-soft">{caption}</div>}
      </div>
      {children}
    </div>
  );
}

function ModuleRow({ module, enabled, locked, onToggle, saving, readOnly }) {
  return (
    <div className={`rounded-2xl border px-4 py-3 flex items-start justify-between gap-3 transition-colors ${
      enabled ? "bg-white border-[#E8EAE9]" : "bg-linen/40 border-[#E8EAE9]"
    }`} data-testid={`mm-row-${module.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm truncate">{module.label}</span>
          {locked && <Lock className="w-3 h-3 text-ink-soft" />}
        </div>
        <div className="text-xs text-ink-mid mt-0.5 leading-relaxed">{module.desc}</div>
      </div>
      <div className="shrink-0">
        {locked ? (
          <div className="text-[10px] uppercase tracking-widest text-ink-soft" data-testid={`mm-locked-${module.id}`}>Always on</div>
        ) : (
          <ToggleSwitch checked={enabled} disabled={readOnly || saving}
                        onChange={() => onToggle(!enabled)}
                        testid={`mm-toggle-${module.id}`} />
        )}
      </div>
    </div>
  );
}

/** Rounded pill toggle — matches the Viyora aesthetic. */
function ToggleSwitch({ checked, onChange, disabled, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      disabled={disabled}
      data-testid={testid}
      data-checked={checked ? "1" : "0"}
      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
        checked ? "bg-pine" : "bg-[#E0DED6]"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 mt-0.5 rounded-full bg-white shadow transform transition-transform ${
          checked ? "translate-x-[22px]" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}
